/* eslint-disable @typescript-eslint/no-explicit-any */
import { DetailErrorType } from "@/constants/record";
import { h5OriginArr, MeetingRecordVisibility, Origin, OSPermissionChoose, PermissionChoose } from "@/constants/share";
import { getOSShareScope, getShareScope, verifyCompanyShare, verifyOSSharePassword, verifySharePassword } from "@/services/share";
import { getMeetingViewableOrganization } from "@/services/summary";
import summaryCommonStore from "@/store/summaryCommon.store";
import userInfoStore, { getUser } from "@/store/user.store";
import { CompanyInfo } from "@/types/company";
import formatOrigin from "@/utils/formatOrigin";
import { message } from "antd";
import { useEffect, useRef, useState } from "react";

export type shareType = "initCheckError" | "expired" | "noExist" | "isDelete" | "orgDenied";

const useShare = () => {
  const [loading, setLoading] = useState(true);
  const [needPassword, setNeedPassword] = useState(true);
  const [shareType, setMyShareType] = useState<shareType>();
  const [viewableOrg, setViewableOrg] = useState<CompanyInfo | null>(null);
  const shareScopeRef = useRef<PermissionChoose | OSPermissionChoose>();
  const streamNameRef = useRef('');
  const shareTypeRef = useRef<shareType>();
  const setShareType = (type: shareType) => {
    shareTypeRef.current = type;
    setMyShareType(type);
  }

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const streamId = searchParams.get("streamId");
    streamNameRef.current = `live/${streamId}`;
  }, [])

  // 个人端企业查询权限
  const checkCompanyValid = async () => {
    setShareType("orgDenied");
    setViewableOrg(null);
    try {
      const viewableOrgRes = await getMeetingViewableOrganization(streamNameRef.current);
      if (!viewableOrgRes.data.length) {
        // 没有组织权限
        return;
      }
      setViewableOrg(viewableOrgRes.data[0]);
    } catch (error) {
      // 没有组织权限
      console.log('checkCompanyValid', error);
    }
  }

  const checkHasError = (error: any, originType: Origin, type = "checkNeed") => {
    console.log('checkHasError', error)
    if (error.errCode === 40002147) {
      checkCompanyValid();
      return true;
    }
    if (error.code === 40002112 || error.code === 40006117) {
      setShareType("noExist");
      return true;
    }
    if (error.code == DetailErrorType.deleted || error.code == 40006122) {
      setShareType("isDelete");
      return true;
    }
    if (error.errCode === 40002113 || error.code === 40002113 || error.code === 40006107) {
      if (type === "checkPassword") {
        message.error("密码错误");
        return true;
      }
      setNeedPassword(true);
      return true;
    }
    // 分享码过期错误码（个人端）：40002141,（大板端）：40006141
    if ((error.code === 40002141 && originType === Origin.MAX_LINK) || (error.code === 40006141 && originType === Origin.MEETING_PLATE)) {
      setShareType("expired");
      return true;
    }
    return false;
  };


  const handleVerifyCompanyShare = async (shareId: string) => {
    await verifyCompanyShare({ shareId: shareId || "", password: "" });
  }

  // 大板端企业查询权限
  const checkOSCompanyValid = async (streamName: string) => {
    try {
      setViewableOrg(null);
      await getUser();
      const viewableOrgRes = await getMeetingViewableOrganization(streamName);
      if (viewableOrgRes.data.length !== 1) {
        // 没有组织权限
        setShareType("orgDenied");
        return;
      }
      const viewableIdentityId = viewableOrgRes.data[0].identityId;
      const currentIdentityId = userInfoStore.getState().company?.identityId;
      // 当前登录企业没有权限，建议切换到对应企业
      if (viewableIdentityId !== currentIdentityId) {
        setViewableOrg(viewableOrgRes.data[0]);
        setShareType("orgDenied");
        return;
      }
    } catch (error) {
      // 没有组织权限
      setShareType("orgDenied");
    }
  }

  const checkNeedPassword = async () => {
    setLoading(true);
    const searchParams = new URLSearchParams(location.search);
    const shareId = searchParams.get("shareId");
    const origin = searchParams.get("origin") as Origin;
    const needPassword = searchParams.get("needPassword");


    const newOrigin = formatOrigin(origin);
    if (!h5OriginArr.includes(newOrigin)) {
      setLoading(false);
      setShareType("noExist");
      return;
    }
    try {
      // 重置分享范围
      summaryCommonStore.getState().setShareScope(MeetingRecordVisibility.all);

      // 1. 获取分享范围
      if (newOrigin === Origin.MAX_LINK) {
        const res = await getShareScope(streamNameRef.current);
        const shareScope = res.data.shareScope;
        shareScopeRef.current = shareScope;
        if (shareId && (shareScope === PermissionChoose.StaffCheck || shareScope === PermissionChoose.StaffPassword)) {
          await handleVerifyCompanyShare(shareId);
          // 个人分享的会跳转过去详情页，不需要进入预览页
        }
      } else if (newOrigin === Origin.MEETING_PLATE) {
        const res = await getOSShareScope(streamNameRef.current);
        const shareScope = res.data.meetingRecordVisibility;
        shareScopeRef.current = shareScope as OSPermissionChoose;
        if (shareScope === MeetingRecordVisibility.orgMember) {
          summaryCommonStore.getState().setShareScope(shareScope);
          await checkOSCompanyValid(streamNameRef.current);
        }
      }
      console.log('shareTypeRef.current', shareTypeRef.current);
      if (shareTypeRef.current) return;

      // 2. 密码校验
      if (needPassword === 'false') {
        setNeedPassword(false);
        searchParams.delete('needPassword');
        const newUrl = `${window.location.pathname}?${searchParams.toString()}`;
        window.history.replaceState(null, '', newUrl);
        return;
      }
      // 非企业的情况下, 才需要调用open的密码校验接口
      if (newOrigin === Origin.MAX_LINK && (shareScopeRef.current === PermissionChoose.Check || shareScopeRef.current === PermissionChoose.Password)) {
        await verifySharePassword({ shareId: shareId || "", password: "" });
      } else if (newOrigin === Origin.MEETING_PLATE) {
        await verifyOSSharePassword({ streamName: streamNameRef.current, password: "" });
      }
      setNeedPassword(false);
    } catch (error: any) {
      if (checkHasError(error, newOrigin)) return;
      setShareType("initCheckError");
      message.error(error.errMsg || "检查分享信息异常");
    } finally {
      setLoading(false);
    }
  };

  const checkPassword = async (password: string, successCb: (password: string) => void) => {
    const queryParams = new URLSearchParams(location.search);
    const shareId = queryParams.get("shareId");
    const origin = queryParams.get("origin") as Origin;
    const newOrigin = formatOrigin(origin);
    try {
      if (newOrigin === Origin.MAX_LINK) {
        if (shareScopeRef.current === PermissionChoose.Check || shareScopeRef.current === PermissionChoose.Password) {
          await verifySharePassword({ shareId: shareId || "", password });
        } else {
          await verifyCompanyShare({ shareId: shareId || "", password })
        }
      } else if (newOrigin === Origin.MEETING_PLATE) {
        await verifyOSSharePassword({ streamName: streamNameRef.current, password });
      } else {
        throw new Error("未知分享类型");
      }
      successCb(password);
    } catch (error: any) {
      if (checkHasError(error, newOrigin, "checkPassword")) return;
      message.error("校验密码异常");
    }
  };

  useEffect(() => {
    checkNeedPassword();
  }, []);

  return {
    loading,
    needPassword,
    checkPassword,
    checkNeedPassword,
    shareType,
    viewableOrg
  };
};

export default useShare;
